import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { t, useLanguage } from '@/app/utils/i18n';
import { DEMO_GROWTH_COHORT_ID, getDemoGrowthDataset } from '@/app/utils/vision-demo-data';
import {
  fetchActiveCalibration,
  fetchClients,
  fetchCohorts,
  fetchDashboardSeries,
  fetchLatestDayMeasurements,
  fetchLocations,
  fetchPhases,
  fetchProjects,
  fetchRecentCaptures,
  fetchRecentMeasurements,
  fetchSpecies,
  fetchZones,
  isoDaysAgo,
  refreshGrowthMetricDay,
  todayIso,
  type AqlCohort,
  type AqlGrowthCalibration,
  type AqlGrowthDashboardPoint,
  type AqlGrowthMeasurement,
  type AqlOption,
  type AqlVisionCapture,
  type GrowthFilters
} from '@/app/services/growth-service';
import { CalibrationStatusCard } from './growth-monitor/calibration-status-card';
import { GrowthKpiCards } from './growth-monitor/growth-kpi-cards';
import { GrowthMeasurementsTable } from './growth-monitor/growth-measurements-table';
import { GrowthTimeline } from './growth-monitor/growth-timeline';
import { GrowthTrendChart } from './growth-monitor/growth-trend-chart';
import { MeasurementQualityPanel } from './growth-monitor/measurement-quality-panel';
import { SizeDistributionChart } from './growth-monitor/size-distribution-chart';

interface VisionGrowthMonitorProps {
  onBack?: () => void;
  embedded?: boolean;
  demoMode?: boolean;
  scope?: {
    clientId?: string | null;
    projectId?: string | null;
    species?: string | null;
    phase?: string | null;
    site?: string | null;
    tank?: string | null;
  };
}

type RangePreset = '7d' | '30d' | '90d' | 'custom';

interface FilterState {
  clientId?: string;
  projectId?: string;
  locationId?: string;
  zoneId?: string;
  cohortId?: string;
  speciesId?: string;
  phaseId?: string;
  rangePreset: RangePreset;
  customFrom?: string;
  customTo?: string;
}

const RANGE_DAYS: Record<Exclude<RangePreset, 'custom'>, number> = {
  '7d': 7,
  '30d': 30,
  '90d': 90
};

const DEFAULT_FILTERS: FilterState = {
  rangePreset: '30d'
};

const resolveRange = (state: FilterState) => {
  if (state.rangePreset === 'custom' && state.customFrom && state.customTo) {
    return { fromDate: state.customFrom, toDate: state.customTo };
  }
  const days = state.rangePreset === 'custom' ? 30 : RANGE_DAYS[state.rangePreset];
  return { fromDate: isoDaysAgo(days), toDate: todayIso() };
};

const buildGrowthFilters = (state: FilterState): GrowthFilters => {
  const { fromDate, toDate } = resolveRange(state);
  return {
    clientId: state.clientId,
    projectId: state.projectId,
    locationId: state.locationId,
    zoneId: state.zoneId,
    cohortId: state.cohortId,
    speciesId: state.speciesId,
    phaseId: state.phaseId,
    fromDate,
    toDate
  };
};

const normalizeLookupValue = (value: string | null | undefined) =>
  (value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const findOptionByLabel = (options: AqlOption[], value: string | null | undefined) => {
  const normalizedValue = normalizeLookupValue(value);
  if (!normalizedValue) return undefined;
  return options.find((option) =>
    normalizeLookupValue(option.name) === normalizedValue ||
    normalizeLookupValue(option.code) === normalizedValue
  );
};

interface DataState {
  loading: boolean;
  error: string | null;
  series: AqlGrowthDashboardPoint[];
  latestDayMeasurements: AqlGrowthMeasurement[];
  recentMeasurements: AqlGrowthMeasurement[];
  captures: AqlVisionCapture[];
  calibration: AqlGrowthCalibration | null;
}

const EMPTY_DATA: DataState = {
  loading: true,
  error: null,
  series: [],
  latestDayMeasurements: [],
  recentMeasurements: [],
  captures: [],
  calibration: null
};

interface FilterSelectProps {
  label: string;
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  options: AqlOption[];
  placeholder?: string;
  disabled?: boolean;
}

function FilterSelect({ label, value, onChange, options, placeholder = 'All', disabled }: FilterSelectProps) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
      <select
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value || undefined)}
        disabled={disabled || options.length === 0}
        className="rounded-lg border border-border bg-input-background px-2.5 py-2 text-sm text-foreground outline-none focus:border-primary disabled:opacity-50"
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.name}
          </option>
        ))}
      </select>
    </label>
  );
}

export function VisionGrowthMonitor({ onBack, embedded = false, demoMode = false, scope }: VisionGrowthMonitorProps) {
  const language = useLanguage();
  const tr = useCallback((key: string, params?: Record<string, unknown>) => t('vision', key, language, params), [language]);
  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const [refreshKey, setRefreshKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState<DataState>(EMPTY_DATA);

  // Filter option sources
  const [clients, setClients] = useState<AqlOption[]>([]);
  const [projects, setProjects] = useState<AqlOption[]>([]);
  const [locations, setLocations] = useState<AqlOption[]>([]);
  const [zones, setZones] = useState<AqlOption[]>([]);
  const [cohorts, setCohorts] = useState<AqlCohort[]>([]);
  const [species, setSpecies] = useState<AqlOption[]>([]);
  const [phases, setPhases] = useState<AqlOption[]>([]);
  const [filterError, setFilterError] = useState<string | null>(null);

  // Load top-level filter sources
  useEffect(() => {
    if (demoMode) {
      const demoData = getDemoGrowthDataset();
      setClients(demoData.clients);
      setProjects(demoData.projects);
      setLocations(demoData.locations);
      setZones(demoData.zones);
      setSpecies(demoData.species);
      setPhases(demoData.phases);
      setCohorts(demoData.cohorts);
      setFilterError(null);
      return;
    }

    let active = true;
    (async () => {
      try {
        const [clientsData, speciesData, phasesData] = await Promise.all([
          fetchClients(),
          fetchSpecies(),
          fetchPhases()
        ]);
        if (!active) return;
        setClients(clientsData);
        setSpecies(speciesData);
        setPhases(phasesData);
      } catch (err) {
        if (!active) return;
        setFilterError(err instanceof Error ? err.message : tr('failedLoadFilters'));
      }
    })();
    return () => {
      active = false;
    };
  }, [demoMode, tr]);

  useEffect(() => {
    if (demoMode) return;

    let active = true;
    (async () => {
      try {
        const result = await fetchProjects(filters.clientId);
        if (active) setProjects(result);
      } catch (err) {
        if (active) setFilterError(err instanceof Error ? err.message : tr('failedLoadProjects'));
      }
    })();
    return () => {
      active = false;
    };
  }, [demoMode, filters.clientId, tr]);

  useEffect(() => {
    if (demoMode) return;

    let active = true;
    (async () => {
      try {
        const result = await fetchLocations(filters.clientId, filters.projectId);
        if (active) setLocations(result);
      } catch (err) {
        if (active) setFilterError(err instanceof Error ? err.message : tr('failedLoadLocations'));
      }
    })();
    return () => {
      active = false;
    };
  }, [demoMode, filters.clientId, filters.projectId, tr]);

  useEffect(() => {
    if (demoMode) return;

    let active = true;
    (async () => {
      try {
        const result = await fetchZones({
          clientId: filters.clientId,
          projectId: filters.projectId,
          locationId: filters.locationId
        });
        if (active) setZones(result);
      } catch (err) {
        if (active) setFilterError(err instanceof Error ? err.message : tr('failedLoadZones'));
      }
    })();
    return () => {
      active = false;
    };
  }, [demoMode, filters.clientId, filters.projectId, filters.locationId, tr]);

  useEffect(() => {
    if (demoMode) return;

    let active = true;
    (async () => {
      try {
        const result = await fetchCohorts({
          clientId: filters.clientId,
          projectId: filters.projectId,
          locationId: filters.locationId,
          zoneId: filters.zoneId,
          speciesId: filters.speciesId,
          phaseId: filters.phaseId
        });
        if (active) setCohorts(result);
      } catch (err) {
        if (active) setFilterError(err instanceof Error ? err.message : tr('failedLoadCohorts'));
      }
    })();
    return () => {
      active = false;
    };
  }, [
    filters.clientId,
    filters.projectId,
    filters.locationId,
    filters.zoneId,
    filters.speciesId,
    filters.phaseId,
    demoMode,
    tr
  ]);

  useEffect(() => {
    if (!scope) return;

    const scopedClientId = scope.clientId ?? undefined;
    const scopedLocationId = scope.site ? findOptionByLabel(locations, scope.site)?.id : undefined;
    const scopedZoneId = scope.tank ? findOptionByLabel(zones, scope.tank)?.id : undefined;
    const scopedSpeciesId = scope.species ? findOptionByLabel(species, scope.species)?.id : undefined;
    const scopedPhaseId = scope.phase ? findOptionByLabel(phases, scope.phase)?.id : undefined;

    setFilters((prev) => {
      const next: FilterState = {
        ...prev,
        clientId: scopedClientId,
        locationId: scopedLocationId,
        zoneId: scopedZoneId,
        speciesId: scopedSpeciesId,
        phaseId: scopedPhaseId
      };

      if (
        next.clientId === prev.clientId &&
        next.locationId === prev.locationId &&
        next.zoneId === prev.zoneId &&
        next.speciesId === prev.speciesId &&
        next.phaseId === prev.phaseId
      ) {
        return prev;
      }

      if (next.zoneId !== prev.zoneId || next.speciesId !== prev.speciesId || next.phaseId !== prev.phaseId) {
        next.cohortId = undefined;
      }

      return next;
    });
  }, [
    locations,
    phases,
    scope,
    scope?.clientId,
    scope?.phase,
    scope?.site,
    scope?.species,
    scope?.tank,
    species,
    zones
  ]);

  useEffect(() => {
    if (!filters.cohortId) return;
    if (cohorts.some((cohort) => cohort.id === filters.cohortId)) return;
    setFilters((prev) => ({ ...prev, cohortId: undefined }));
  }, [cohorts, filters.cohortId]);

  // Main data load
  const growthFilters = useMemo(() => buildGrowthFilters(filters), [filters]);

  useEffect(() => {
    if (demoMode) {
      const demoData = getDemoGrowthDataset(growthFilters);
      setData({
        loading: false,
        error: null,
        series: demoData.series,
        latestDayMeasurements: demoData.latestDayMeasurements,
        recentMeasurements: demoData.recentMeasurements,
        captures: demoData.captures,
        calibration: demoData.calibration
      });
      return;
    }

    let active = true;
    setData((prev) => ({ ...prev, loading: true, error: null }));

    (async () => {
      try {
        const [series, recentMeasurements, captures, calibration] = await Promise.all([
          fetchDashboardSeries(growthFilters),
          fetchRecentMeasurements({
            cohortId: growthFilters.cohortId,
            zoneId: growthFilters.zoneId,
            limit: 250
          }),
          fetchRecentCaptures({
            cohortId: growthFilters.cohortId,
            zoneId: growthFilters.zoneId,
            limit: 8
          }),
          fetchActiveCalibration({ zoneId: growthFilters.zoneId })
        ]);

        if (!active) return;

        const latestPeriod = series.length ? series[series.length - 1].period_start.slice(0, 10) : undefined;
        const latestDayMeasurements = await fetchLatestDayMeasurements({
          cohortId: growthFilters.cohortId,
          zoneId: growthFilters.zoneId,
          day: latestPeriod,
          limit: 1000
        });

        if (!active) return;
        setData({
          loading: false,
          error: null,
          series,
          latestDayMeasurements,
          recentMeasurements,
          captures,
          calibration
        });
      } catch (err) {
        if (!active) return;
        setData({
          ...EMPTY_DATA,
          loading: false,
          error: err instanceof Error ? err.message : tr('failedLoadGrowthData')
        });
      }
    })();

    return () => {
      active = false;
    };
  }, [demoMode, growthFilters, refreshKey, tr]);

  useEffect(() => {
    if (!demoMode) return;
    const interval = window.setInterval(() => setRefreshKey((value) => value + 1), 15_000);
    return () => window.clearInterval(interval);
  }, [demoMode]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      if (!demoMode && (filters.cohortId || filters.zoneId)) {
        await refreshGrowthMetricDay({
          cohortId: filters.cohortId,
          zoneId: filters.zoneId
        });
      }
    } catch (err) {
      setData((prev) => ({
        ...prev,
        error: err instanceof Error ? err.message : tr('failedRefreshGrowthMetrics')
      }));
    } finally {
      setRefreshKey((value) => value + 1);
      setRefreshing(false);
    }
  }, [demoMode, filters.cohortId, filters.zoneId, tr]);

  const cohortOptions = useMemo<AqlOption[]>(
    () => cohorts.map((cohort) => ({
      id: cohort.id,
      name: demoMode && cohort.id === DEMO_GROWTH_COHORT_ID
        ? tr('demoGrowthCohort')
        : cohort.name ?? cohort.id.slice(0, 8)
    })),
    [cohorts, demoMode, tr]
  );

  const updateFilter = useCallback(<K extends keyof FilterState>(key: K, value: FilterState[K]) => {
    setFilters((prev) => {
      const next: FilterState = { ...prev, [key]: value };
      // Cascade resets so that downstream filters don't reference stale parents.
      if (key === 'clientId') {
        next.projectId = undefined;
        next.locationId = undefined;
        next.zoneId = undefined;
        next.cohortId = undefined;
      } else if (key === 'projectId') {
        next.locationId = undefined;
        next.zoneId = undefined;
        next.cohortId = undefined;
      } else if (key === 'locationId') {
        next.zoneId = undefined;
        next.cohortId = undefined;
      } else if (key === 'zoneId') {
        next.cohortId = undefined;
      }
      return next;
    });
  }, []);

  const hasSelection = Boolean(filters.zoneId || filters.cohortId);

  const specificFilters = (
    <section className="rounded-2xl border border-border bg-card p-4 shadow-sm">
      <div className={`grid grid-cols-1 gap-3 ${embedded ? 'md:grid-cols-2' : 'md:grid-cols-4 xl:grid-cols-8'}`}>
        {!embedded ? (
          <>
            <FilterSelect
              label={tr('clientFacility')}
              value={filters.clientId}
              onChange={(value) => updateFilter('clientId', value)}
              options={clients}
              placeholder={tr('all')}
            />
            <FilterSelect
              label={tr('project')}
              value={filters.projectId}
              onChange={(value) => updateFilter('projectId', value)}
              options={projects}
              disabled={!filters.clientId && projects.length === 0}
              placeholder={tr('all')}
            />
            <FilterSelect
              label={tr('facility')}
              value={filters.locationId}
              onChange={(value) => updateFilter('locationId', value)}
              options={locations}
              placeholder={tr('all')}
            />
            <FilterSelect
              label={tr('zoneTank')}
              value={filters.zoneId}
              onChange={(value) => updateFilter('zoneId', value)}
              options={zones}
              placeholder={tr('all')}
            />
          </>
        ) : null}
        <FilterSelect
          label={tr('cohort')}
          value={filters.cohortId}
          onChange={(value) => updateFilter('cohortId', value)}
          options={cohortOptions}
          placeholder={tr('all')}
        />
        {!embedded ? (
          <>
            <FilterSelect
              label={tr('species')}
              value={filters.speciesId}
              onChange={(value) => updateFilter('speciesId', value)}
              options={species}
              placeholder={tr('all')}
            />
            <FilterSelect
              label={tr('phase')}
              value={filters.phaseId}
              onChange={(value) => updateFilter('phaseId', value)}
              options={phases}
              placeholder={tr('all')}
            />
          </>
        ) : null}
        <label className="flex flex-col gap-1">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{tr('timeRange')}</span>
          <select
            value={filters.rangePreset}
            onChange={(event) =>
              setFilters((prev) => ({ ...prev, rangePreset: event.target.value as RangePreset }))
            }
            className="rounded-lg border border-border bg-input-background px-2.5 py-2 text-sm text-foreground outline-none focus:border-primary"
          >
            <option value="7d">{tr('sevenDays')}</option>
            <option value="30d">{tr('thirtyDays')}</option>
            <option value="90d">{tr('ninetyDays')}</option>
            <option value="custom">{tr('customRange')}</option>
          </select>
        </label>
      </div>

      {filters.rangePreset === 'custom' ? (
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{tr('fromDate')}</span>
            <input
              type="date"
              value={filters.customFrom ?? isoDaysAgo(30)}
              onChange={(event) => setFilters((prev) => ({ ...prev, customFrom: event.target.value }))}
              className="rounded-lg border border-border bg-input-background px-2.5 py-2 text-sm text-foreground outline-none focus:border-primary"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{tr('toDate')}</span>
            <input
              type="date"
              value={filters.customTo ?? todayIso()}
              onChange={(event) => setFilters((prev) => ({ ...prev, customTo: event.target.value }))}
              className="rounded-lg border border-border bg-input-background px-2.5 py-2 text-sm text-foreground outline-none focus:border-primary"
            />
          </label>
        </div>
      ) : null}
    </section>
  );

  return (
    <div data-demo-tour="growth" className={embedded ? 'space-y-4 text-foreground' : 'min-h-screen bg-background text-foreground'}>
      <header className={embedded ? 'rounded-2xl border border-border bg-card p-4 shadow-sm' : 'sticky top-0 z-30 border-b border-border bg-background/92 px-4 py-3 backdrop-blur-md sm:px-6'}>
        <div className={embedded ? 'flex flex-wrap items-center gap-3' : 'mx-auto flex max-w-[1680px] flex-wrap items-center gap-3'}>
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-card text-foreground hover:bg-accent"
              aria-label={t('common', 'back', language)}
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
          ) : null}
          <div className="min-w-0">
            <h1 className="text-base font-semibold leading-tight sm:text-lg">{tr('growthTitle')}</h1>
            <p className="text-xs text-muted-foreground sm:text-sm">
              {tr('growthSubtitle')}
            </p>
          </div>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing}
            className="ml-auto inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-xs font-semibold text-foreground hover:bg-accent disabled:opacity-60"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            {tr('refresh')}
          </button>
        </div>
      </header>

      <div className={embedded ? 'space-y-4' : 'mx-auto max-w-[1680px] space-y-4 px-4 py-4 sm:px-6'}>
        {filterError ? (
          <div className="rounded-2xl border border-amber-400/40 bg-amber-500/10 p-3 text-xs text-amber-800 dark:text-amber-100">
            {filterError}
          </div>
        ) : null}

        {specificFilters}

        {data.error ? (
          <div className="rounded-2xl border border-red-500/40 bg-red-500/10 p-3 text-xs text-red-700 dark:text-red-100">
            {data.error}
          </div>
        ) : null}

        <GrowthKpiCards series={data.series} loading={data.loading} />

        <section className="grid gap-4 xl:grid-cols-[1.4fr_1fr]">
          <GrowthTrendChart series={data.series} loading={data.loading} />
          <div className="space-y-4">
            <MeasurementQualityPanel series={data.series} loading={data.loading} />
            <CalibrationStatusCard calibration={data.calibration} loading={data.loading} />
          </div>
        </section>

        <section className="grid gap-4 xl:grid-cols-[minmax(520px,1.1fr)_minmax(360px,0.9fr)]">
          <SizeDistributionChart measurements={data.latestDayMeasurements} loading={data.loading} />
          <GrowthTimeline captures={data.captures} series={data.series} loading={data.loading} />
        </section>

        <GrowthMeasurementsTable measurements={data.recentMeasurements} loading={data.loading} />

        {!hasSelection && !data.loading && data.series.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border bg-muted/30 p-6 text-center text-sm text-muted-foreground">
            {tr('selectZoneOrCohortGrowth')}
          </div>
        ) : null}
      </div>
    </div>
  );
}
